import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    Platform,
    Image,
    Alert,
    ActivityIndicator,
    KeyboardAvoidingView,
    ScrollView,
    Keyboard,
    TouchableWithoutFeedback,
} from 'react-native';
import TabBar from '../../components/TabBar';
import { v2ChangePassword } from '../../services/v2/api';
import { clearBiometricCredentials } from '../../services/biometrics';
import PasswordInput from '../../components/UIComponents/PasswordInput';
import SimpleHeader from '../../components/SimpleHeader';
import { COLORS } from '../../theme/themeTokens';
import { changePasswordErrorMessage } from './changePasswordError';

type TabScreen = 'dashboard' | 'products' | 'reports' | 'support' | 'profile';

interface ChangePasswordScreenProps {
    onBack: () => void;
    onNavigate: (screen: TabScreen) => void;
    onPasswordChanged: () => void; // Called after successful password change to force re-login
}

const ChangePasswordScreen: React.FC<ChangePasswordScreenProps> = ({
    onBack,
    onNavigate,
    onPasswordChanged,
}) => {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [mismatchError, setMismatchError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mismatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Show mismatch error 1 second after user stops typing, hide while editing
    useEffect(() => {
        setMismatchError(null);
        if (mismatchTimerRef.current) {
            clearTimeout(mismatchTimerRef.current);
        }
        if (newPassword && confirmPassword) {
            mismatchTimerRef.current = setTimeout(() => {
                if (newPassword !== confirmPassword) {
                    setMismatchError('Passwords do not match');
                }
            }, 1000);
        }
        return () => {
            if (mismatchTimerRef.current) {
                clearTimeout(mismatchTimerRef.current);
            }
        };
    }, [newPassword, confirmPassword]);

    const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
    const isButtonDisabled = isLoading || !passwordsMatch;

    const handleChangePassword = async () => {
        // Validation
        if (!oldPassword.trim()) {
            setError('Please enter your current password');
            return;
        }
        if (!newPassword.trim()) {
            setError('Please enter a new password');
            return;
        }
        if (newPassword.length < 8) {
            setError('New password must be at least 8 characters');
            return;
        }
        if (oldPassword === newPassword) {
            setError('New password must be different from current password');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setError(null);
        setIsLoading(true);

        try {
            await v2ChangePassword(oldPassword, newPassword);

            // Clear biometric credentials since they're now invalid
            // (password changed, session invalidated)
            await clearBiometricCredentials();

            Alert.alert(
                'Password Changed',
                'Your password has been changed successfully. Please log in again with your new password.',
                [
                    {
                        text: 'OK',
                        onPress: () => onPasswordChanged(),
                    },
                ]
            );
        } catch (err) {
            setError(changePasswordErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <View style={styles.container}>
            {/* Header */}
            <SimpleHeader title="Change password" onBack={onBack} backgroundVariant='white' />

            {/* Main Content */}
            <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
                <KeyboardAvoidingView
                    style={styles.mainContent}
                    behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                    keyboardVerticalOffset={0}>
                    <ScrollView
                        style={styles.scrollView}
                        contentContainerStyle={styles.scrollContent}
                        showsVerticalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled">

                        {/* Title Row */}
                    {/* <View style={styles.titleRow}>
                        <Image
                            source={require('../../assets/profile-change-password.png')}
                            style={styles.titleIcon}
                            resizeMode="contain"
                        />
                        <Text style={styles.title}>Change password</Text>
                    </View> */}

                    {/* Old Password Input */}
                    <PasswordInput
                        value={oldPassword}
                        onChangeText={setOldPassword}
                        placeholder="Last password"
                        editable={!isLoading}
                        style={styles.passwordField}
                    />

                    {/* New Password Input */}
                    <PasswordInput
                        value={newPassword}
                        onChangeText={setNewPassword}
                        placeholder="New password"
                        editable={!isLoading}
                        style={styles.passwordField}
                    />

                    {/* Confirm New Password Input */}
                    <PasswordInput
                        value={confirmPassword}
                        onChangeText={setConfirmPassword}
                        placeholder="Confirm new password"
                        editable={!isLoading}
                        hasError={!!mismatchError}
                        style={styles.passwordField}
                    />

                    {/* Mismatch Error */}
                    {mismatchError && (
                        <Text style={styles.mismatchText}>{mismatchError}</Text>
                    )}

                    {/* Error Message */}
                    {error && (
                        <Text style={styles.errorText}>{error}</Text>
                    )}

                    {/* Change Password Button */}
                    <TouchableOpacity
                        style={[
                            styles.changeButton,
                            isButtonDisabled && styles.changeButtonDisabled,
                        ]}
                        onPress={handleChangePassword}
                        disabled={isButtonDisabled}>
                        {isLoading ? (
                            <ActivityIndicator color={COLORS.white} />
                        ) : (
                            <Text style={styles.changeButtonText}>Change password</Text>
                        )}
                        </TouchableOpacity>
                    </ScrollView>
                </KeyboardAvoidingView>
            </TouchableWithoutFeedback>

            {/* Tab Bar */}
            <TabBar activeTab="dashboard" onNavigate={onNavigate} />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.primaryNavy,
    },

    mainContent: {
        flex: 1,
        backgroundColor: COLORS.white,
        // borderTopLeftRadius: 12,
        // borderTopRightRadius: 12,
        paddingTop: 20,
        paddingLeft: 10,
        paddingRight: 10,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        padding: 20,
        paddingBottom: 120,
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 24,
    },
    titleIcon: {
        width: 24,
        height: 24,
        tintColor: COLORS.steelNavy,
    },
    title: {
        fontFamily: 'Inter',
        fontSize: 18,
        fontWeight: '500',
        color: COLORS.deepNavy,
    },
    passwordField: {
        marginBottom: 16,
    },
    mismatchText: {
        fontFamily: 'Inter',
        fontSize: 15,
        color: COLORS.brandOrange,
        marginBottom: 12,
    },
    errorText: {
        fontFamily: 'Inter',
        fontSize: 16,
        color: COLORS.brandOrange,
        marginBottom: 16,
    },
    changeButton: {
        backgroundColor: COLORS.brandOrange,
        borderRadius: 4,
        height: 52,
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 8,
    },
    changeButtonDisabled: {
        opacity: 0.7,
    },
    changeButtonText: {
      lineHeight: 22,
      fontFamily: 'Inter',
      fontSize: 16,
      fontWeight: '500',
        color: COLORS.white,
    },
});

export default ChangePasswordScreen;

